import axios, { AxiosProgressEvent } from 'axios';
import { ValidationError } from 'request-sender';
import { useBackendApi } from './backendApi';
import { useFileValidator } from './fileValidator';

export enum FileType {
  PROJECT_DOCUMENT = 'PROJECT_DOCUMENT',
  PROJECT_PHOTO = 'PROJECT_PHOTO',
  COMPANY_LOGO = 'COMPANY_LOGO',
}

export interface FileDto {
  id: number,
  name: string,
  alias: string,
  mimeType: string,
  size: number,
}

export interface DownloadFileDto {
  downloadUrl: string,
  fileName: string
  size: number
  mimeType: string
}

export interface CreateUploadLinkResponse {
  uploadLink: string,
  file: FileDto,
}

export function useMimeTypesHelper() {

  const isPdfMimeType = (mimeType: string) => {
    return mimeType === 'application/pdf';
  };

  const isWordMimeType = (mimeType: string) => {
    return [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
    ].includes(mimeType);
  };

  return {
    isPdfMimeType,
    isWordMimeType,
  };
}

async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const fileData = reader.result as ArrayBuffer;
      resolve(fileData);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
}

export function useFileService() {
  const nekstApi = useBackendApi();

  const fileValidator = useFileValidator();

  const createFileUploadLink = async (
    fileType: FileType,
    fileName: string,
    asAdmin?: boolean,
  ) => {
    return await nekstApi.post('/files/uploadlinks', {
      fileType,
      fileName,
      asAdmin,
    }) as CreateUploadLinkResponse;
  };

  function getFileContentType(file: File): string {
    return file.type || 'application/octet-stream';
  }

  const doUploadFile = async (
    file: File,
    fileName: string,
    uploadLink: string,
    onProgress?: (value: number) => void,
  ) => {
    const fileData = await readFileAsArrayBuffer(file);

    const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
      const {
        loaded,
        total,
      } = progressEvent;
      if (total) {
        const percent = Math.floor((loaded * 100) / total);
        if (onProgress) {
          onProgress(percent);
        }
      }
    };

    await axios.put(
      uploadLink,
      fileData,
      {
        headers: {
          'Content-Type': getFileContentType(file),
          'Content-Disposition': `attachment; filename="${fileName}"`,
        },
        onUploadProgress,
      },
    );
  };

  const uploadFile = async (
    file: File,
    fileType: FileType,
    fileName: string,
    onUploadProgress?: (value: number) => void,
    asAdmin?: boolean,
  ): Promise<FileDto> => {
    const validationErrors = await fileValidator.validateFile(file, fileType);

    if (validationErrors.length === 0) {
      const uploadLinkResponse = await createFileUploadLink(
        fileType,
        fileName,
        asAdmin,
      );

      await doUploadFile(
        file,
        fileName,
        uploadLinkResponse.uploadLink,
        onUploadProgress,
      );

      return uploadLinkResponse.file;
    } else {
      throw new ValidationError({
        fileId: validationErrors,
      });
    }
  };

  const getFileById = async (fileType: FileType, fileId: number) => {
    return await nekstApi.get('/files/' + fileType.toLowerCase() + '/' + fileId) as FileDto;
  }


  return {
    createFileUploadLink,
    uploadFile,
    getFileById,
  };
}
